import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'station_auth';

export type AuthUser = { role: 'owner' } | { role: 'partner'; partnerId?: number };

function loadStored(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.role === 'owner') return { role: 'owner' };
    if (data?.role === 'partner') return { role: 'partner', partnerId: typeof data?.partnerId === 'number' ? data.partnerId : undefined };
    return null;
  } catch {
    return null;
  }
}

type AuthContextValue = {
  user: AuthUser | null;
  login: (user: AuthUser) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadStored);

  useEffect(() => {
    if (user) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } catch {}
    } else {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }, [user]);

  const login = useCallback((u: AuthUser) => setUser(u), []);
  const logout = useCallback(() => setUser(null), []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
